import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Minus, Trash2, Search, Sparkles, AlertTriangle, ExternalLink, Store, UserRound, Pencil, MessageCircle, Save, Gift } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { CategoryBudget } from "../components/CategoryBudget";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { PageHeader, Loader } from "../components/shared";
import { Modal } from "../components/Modal";
import { MySaves } from "../components/MySaves";
import { SaveChangesBar } from "../components/SaveChangesBar";
import { MoneyInput } from "../components/MoneyInput";
import { openWhatsApp } from "../components/WhatsAppShare";
import ProductResults from "../components/ProductResults";
import { ProductSearchBar } from "../components/ProductSearchBar";
import { PageTutorial } from "../components/PageTutorial";
import { GIFTS_TUTORIAL } from "../tutorials/gifts";
import { useBudgetGuard } from "../components/BudgetGuard";

const PRICE_TIERS = { BRL: [50, 100, 200, 500], USD: [25, 50, 100, 200], EUR: [25, 50, 100, 200] };

export default function Gifts() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const [params] = useSearchParams();
  const [cats, setCats] = useState(null);
  const [cat, setCat] = useState("her");
  const [suggestions, setSuggestions] = useState([]);
  const [priceMax, setPriceMax] = useState(null);
  const [custom, setCustom] = useState("");
  const [prodModal, setProdModal] = useState(null);
  const [saves, setSaves] = useState([]);
  const [giftLimit, setGiftLimit] = useState(null);
  const [draft, setDraft] = useState(null); // { saveId, items } — unsaved edits inside a saved version
  const [savingDraft, setSavingDraft] = useState(false);
  const [list, setList] = useState(() => { try { return JSON.parse(localStorage.getItem("gifts_draft_list") || "[]"); } catch { return []; } }); // new list being built
  const [listName, setListName] = useState("");
  const [savingList, setSavingList] = useState(false);
  const { guard, node: budgetGuardNode } = useBudgetGuard();

  const currency = user?.currency || "BRL";
  const country = user?.country || "BR";
  const symbol = symbolForCurrency(config, currency);
  const forName = params.get("for");
  const wishQ = params.get("q");
  const priceTiers = PRICE_TIERS[currency] || PRICE_TIERS.EUR;
  const effectiveMax = custom ? Number(custom) : priceMax;

  useEffect(() => {
    api.get(`/content/gifts?lang=${lang}`).then(({ data }) => setCats(data.categories)).catch(() => {});
    api.get("/gifts/saves").then(({ data }) => setSaves(data.saves || [])).catch(() => {});
    api.get("/budget").then(({ data }) => {
      const lim = data.budget?.categories?.gifts?.limit;
      if (lim != null) { setGiftLimit(lim); return; }
      api.get(`/christmas?lang=${lang}`).then(({ data: x }) => setGiftLimit(x.computed?.split_amounts?.gifts ?? null)).catch(() => {});
    }).catch(() => {});
  }, [lang]);
  useEffect(() => { try { localStorage.setItem("gifts_draft_list", JSON.stringify(list)); } catch { /* ignore */ } }, [list]);
  useEffect(() => {
    api.get(`/content/gift-suggestions?lang=${lang}&category=${cat}&currency=${currency}`)
      .then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
  }, [cat, lang, currency]);

  if (!cats) return <Loader />;
  const catLabel = (id) => cats.find((c) => c.id === id)?.label || id;
  const shown = effectiveMax ? suggestions.filter((s) => s.price_est == null || s.price_est <= effectiveMax) : suggestions;

  const productItem = (p) => ({ title: p.title, planned: p.extracted_price || 0, link: p.product_link || p.link || null, image: p.thumbnail || null, store: p.source || null });
  const chooseProduct = (p) => {
    const rep = prodModal?.replace;
    const target = rep && rep.saveId !== "__list__" && saves.find((sv) => sv.save_id === rep.saveId);
    if (target) {
      const base = draft?.saveId === target.save_id ? draft.items : (target.items || []);
      setDraft({ saveId: target.save_id, items: base.map((x, j) => (j === rep.index ? { ...x, ...productItem(p) } : x)) });
    } else if (rep?.saveId === "__list__") {
      setList((l) => l.map((x, j) => (j === rep.index ? { ...x, ...productItem(p) } : x)));
    } else {
      setList((l) => [...l, { recipient: forName || catLabel(cat), qty: 1, ...productItem(p) }]);
      toast.success(t("gifts.added_to_list"));
    }
    setProdModal(null);
  };
  const saveList = () => {
    if (!list.length) return;
    if (saves.length >= 3) { toast.error(t("gifts.saves_full")); return; }
    const total = totalOf(list);
    guard("gifts", total, async () => {
      setSavingList(true);
      try {
        const { data } = await api.post("/gifts/saves", { items: list, total, name: listName.trim() || null });
        setSaves(data.saves || []);
        setList([]); setListName("");
        toast.success(t("gifts.list_saved"));
        setTimeout(() => document.querySelector('[data-testid="catbudget-gifts"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      } catch { toast.error("Error"); }
      setSavingList(false);
    });
  };
  const shareSavedWhatsApp = (sv) => {
    const items = itemsOf(sv);
    const when = (() => { try { return new Date(sv.saved_at).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } })();
    const lines = [`🎁 *${sv.name || t("deco.saves_title")}* · ${when}`, ""];
    items.forEach((it) => {
      const q = Math.max(1, Number(it.qty || 1));
      lines.push(`• ${it.title}${q > 1 ? ` ×${q}` : ""}${it.recipient ? ` — ${it.recipient}` : ""} — ${fmt(Number(it.planned || 0) * q)}${it.store ? ` (${it.store})` : ""}`);
      if (it.link) lines.push(`  🔗 ${it.link}`);
      if (it.image) lines.push(`  🖼 ${it.image}`);
    });
    lines.push("", `💰 *${t("gifts.total")}: ${fmt(totalOf(items))}*`, "", "_MY CHRISTMAS_");
    openWhatsApp(lines.join("\n"));
  };
  const patchItems = (s, items) => setDraft({ saveId: s.save_id, items });
  const itemsOf = (s) => (draft?.saveId === s.save_id ? draft.items : (s.items || []));
  const totalOf = (items) => Math.round(items.reduce((a, it) => a + Number(it.planned || 0) * Math.max(1, Number(it.qty || 1)), 0) * 100) / 100;
  const saveDraft = async () => {
    if (!draft) return;
    setSavingDraft(true);
    await patchSave(draft.saveId, { items: draft.items });
    setSavingDraft(false);
    setDraft(null);
  };
  const searchTerm = (title) => (title || "").replace(/[|()\-–].*$/, "").split(/\s+/).slice(0, 3).join(" ").trim() || title;
  const deleteSave = async (id) => {
    try { const { data } = await api.delete(`/gifts/saves/${id}`); setSaves(data.saves || []); } catch { /* ignore */ }
  };
  const patchSave = async (id, body) => {
    try { const { data } = await api.patch(`/gifts/saves/${id}`, body); setSaves(data.saves || []); toast.success(t("saves.updated")); }
    catch { toast.error("Error"); }
  };
  const fmt = (v) => formatMoney(symbol, v || 0);
  const saveOver = (sv) => giftLimit != null ? Math.round((Number(sv.total || 0) - Number(giftLimit)) * 100) / 100 : 0;
  const savedSum = Math.round(saves.reduce((a, s) => a + Number(s.total || 0), 0) * 100) / 100;
  const findReal = async (q, replace = null) => {
    setProdModal({ label: q, loading: true, result: null, replace });
    try {
      const mp = effectiveMax && !replace ? `&max_price=${effectiveMax}` : "";
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${country}${mp}&limit=10&min_results=10&diverse=1`);
      setProdModal({ label: q, loading: false, result: res, replace });
    } catch { setProdModal({ label: q, loading: false, result: { state: "error" }, replace }); }
  };

  const itemRows = (items, setItems, swapKey, tid) => (
        <ul className="space-y-1.5">
          {items.map((it, i) => {
            const qty = Math.max(1, Number(it.qty || 1));
            const setItem = (patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            return (
              <li key={`${i}-${it.title}-${it.planned}`} data-testid={`${tid}-item`} className="flex flex-wrap items-center gap-2 rounded-lg bg-xmas-navy/40 px-3 py-2 text-sm sm:flex-nowrap">
                {it.image
                  ? <a href={it.link || undefined} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`${tid}-image-${i}`}><img src={it.image} alt="" className="h-14 w-14 rounded-lg bg-white object-contain" /></a>
                  : <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-xmas-navysurface" data-testid={`${tid}-image-${i}`}><Store className="h-5 w-5 text-xmas-slate" /></span>}
                <span className="min-w-0 flex-1">
                  {it.link
                    ? <a href={it.link} target="_blank" rel="noreferrer" className="block truncate text-xmas-snow underline-offset-2 hover:text-xmas-gold hover:underline" title={t("prod.view")} data-testid={`${tid}-title-${i}`}>{it.title}</a>
                    : <span className="block truncate text-xmas-snow" data-testid={`${tid}-title-${i}`}>{it.title}</span>}
                  <span className="flex items-center gap-2 text-[11px] text-xmas-slate">
                    {it.store && <span className="inline-flex items-center gap-1"><Store className="h-3 w-3" />{it.store}</span>}
                    <RecipientEdit value={it.recipient} placeholder={t("gifts.recipient_ph")} testid={`${tid}-recipient-${i}`} onChange={(v) => setItem({ recipient: v })} />
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => qty > 1 && setItem({ qty: qty - 1 })} data-testid={`${tid}-qty-minus-${i}`} aria-label="-" className="rounded-full border border-xmas-gold/30 p-1 text-xmas-snow hover:border-xmas-gold disabled:opacity-40" disabled={qty <= 1}><Minus className="h-3 w-3" /></button>
                  <span className="w-6 text-center font-mono-num text-xs text-xmas-snow" data-testid={`${tid}-qty-${i}`}>{qty}</span>
                  <button type="button" onClick={() => setItem({ qty: qty + 1 })} data-testid={`${tid}-qty-plus-${i}`} aria-label="+" className="rounded-full border border-xmas-gold/30 p-1 text-xmas-snow hover:border-xmas-gold"><Plus className="h-3 w-3" /></button>
                  <span className="ml-1 font-mono-num text-xs text-xmas-slate">{symbol}</span>
                  <MoneyInput value={String(it.planned ?? 0)} testid={`${tid}-planned-${i}`}
                    onChange={(v) => setItem({ planned: Number(v || 0) })}
                    className="w-24 rounded-md border border-xmas-gold/30 bg-xmas-navy/60 px-2 py-0.5 text-right font-mono-num text-xs text-xmas-snow focus:border-xmas-gold focus:outline-none" />
                  {qty > 1 && <span className="font-mono-num text-xs text-xmas-champagne" data-testid={`${tid}-line-${i}`}>= {fmt(Number(it.planned || 0) * qty)}</span>}
                  {it.link && <a href={it.link} target="_blank" rel="noreferrer" data-testid={`${tid}-link-${i}`} title={t("prod.view")} aria-label={t("prod.view")} className="rounded-full p-1.5 text-xmas-gold transition hover:bg-xmas-gold/20"><ExternalLink className="h-3.5 w-3.5" /></a>}
                  <button type="button" onClick={() => findReal(searchTerm(it.title), { saveId: swapKey, index: i })} data-testid={`${tid}-find-${i}`} title={t("saves.swap_item")} aria-label={t("saves.swap_item")} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-gold/20 hover:text-xmas-gold"><Search className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`${tid}-remove-${i}`} title={t("saves.remove_item")} aria-label={t("saves.remove_item")} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </li>
            );
          })}
        </ul>
  );

  const savesPanel = (
    <MySaves bare saves={saves} initialViewId={params.get("save")} onDelete={deleteSave} onCloseView={() => setDraft(null)} onRename={(sv, v) => patchSave(sv.save_id, { name: v })} rowValue={(sv) => ({ text: fmt(sv.total), over: saveOver(sv) > 0 })} lang={lang} t={t} testid="gifts-saves" itemLabel={t("saves.items_label")} renderItems={(sv) => {
    const s = { ...sv, items: itemsOf(sv), total: totalOf(itemsOf(sv)) };
    return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className={`font-mono-num text-sm ${saveOver(s) > 0 ? "text-xmas-crimson" : "text-xmas-champagne"}`} data-testid="gift-saved-total">{fmt(s.total)}</p>
        <button type="button" onClick={() => shareSavedWhatsApp(s)} data-testid="gift-saved-share-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 px-4 py-1.5 text-xs font-semibold text-[#25D366] transition hover:bg-[#25D366]/20">
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </button>
      </div>
      {saveOver(s) > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-xmas-crimson/40 bg-xmas-crimson/15 px-4 py-2.5 text-sm text-xmas-snow" data-testid="gift-saved-over-budget">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-xmas-crimson" />
          <span>{t("saves.over_total").replace("{s}", fmt(s.total)).replace("{v}", fmt(saveOver(s))).replace("{b}", fmt(giftLimit))}</span>
        </div>
      )}
      {itemRows(s.items || [], (items) => patchItems(s, items), s.save_id, "gift-saved")}
      {draft?.saveId === s.save_id && <SaveChangesBar t={t} onSave={saveDraft} onDiscard={() => setDraft(null)} saving={savingDraft} testid="gift-saved-changes" />}
    </div>
    );
    }} />
  );

  return (
    <div>
      <PageHeader title={t("gifts.title")} testid="gifts-header" />
      <PageTutorial id="gifts" content={GIFTS_TUTORIAL} />
      <CategoryBudget category="gifts" savedSum={savedSum} aside={savesPanel} />

      {list.length > 0 && (
        <div className="glass mb-8 rounded-2xl border border-xmas-gold/30 p-5" data-testid="gift-list-panel">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 font-display text-lg text-xmas-gold"><Gift className="h-5 w-5" /> {t("gifts.draft_title")} <span className="text-xs font-normal text-xmas-slate">({list.length})</span></h3>
              <p className="text-xs text-xmas-slate">{t("gifts.draft_hint")}</p>
            </div>
            <p className={`font-mono-num text-xl font-bold ${giftLimit != null && totalOf(list) > giftLimit ? "text-xmas-crimson" : "text-xmas-gold"}`} data-testid="gift-list-total">{fmt(totalOf(list))}</p>
          </div>
          {itemRows(list, setList, "__list__", "gift-list")}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input value={listName} onChange={(e) => setListName(e.target.value)} maxLength={60} placeholder={t("gifts.list_name_ph")} data-testid="gift-list-name-input"
              className="w-64 rounded-full border border-xmas-gold/30 bg-xmas-navy/50 px-4 py-3 text-xs text-xmas-snow placeholder:text-xmas-slate focus:border-xmas-gold focus:outline-none" />
            <button type="button" onClick={saveList} disabled={savingList} data-testid="gift-list-save" className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-3 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
              <Save className="h-4 w-4" /> {t("gifts.save_list")}
            </button>
            <button type="button" onClick={() => { setList([]); setListName(""); }} data-testid="gift-list-clear" className="inline-flex items-center gap-2 rounded-full border border-xmas-crimson/40 px-4 py-3 text-xs text-xmas-snow transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
              <Trash2 className="h-4 w-4" /> {t("gifts.clear_list")}
            </button>
          </div>
        </div>
      )}

      {forName && (
        <div className="glass mb-6 flex items-center justify-between gap-3 rounded-2xl border border-xmas-gold/30 p-4" data-testid="gifts-ss-banner">
          <p className="flex items-center gap-2 text-sm text-xmas-champagne"><Sparkles className="h-4 w-4 text-xmas-gold" /> {t("ss.find_gift", { name: forName })}</p>
          {wishQ && <button onClick={() => findReal(wishQ)} className="btn-gold rounded-full px-4 py-2 text-xs font-bold uppercase">{t("gifts.find_real")}</button>}
        </div>
      )}

      {/* Category filter */}
      <p className="mb-2 text-sm text-xmas-slate">{t("gifts.for_whom")}</p>
      <div className="mb-5 flex flex-wrap gap-2">
        {cats.map((c) => (
          <button key={c.id} data-testid={`gift-cat-${c.id}`} onClick={() => setCat(c.id)} className={`rounded-full px-4 py-2 text-sm transition ${cat === c.id ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{c.label}</button>
        ))}
      </div>

      {/* Price filter */}
      <p className="mb-2 text-sm text-xmas-slate">{t("gifts.budget_q")}</p>
      <div className="mb-8 flex flex-wrap items-center gap-2">
        <button onClick={() => { setPriceMax(null); setCustom(""); }} className={`rounded-full px-4 py-2 text-sm transition ${!priceMax && !custom ? "btn-gold" : "border border-xmas-gold/30 text-xmas-slate hover:text-xmas-snow"}`}>{t("gifts.price_any")}</button>
        {priceTiers.map((v) => (
          <button key={v} data-testid={`gift-price-${v}`} onClick={() => { setPriceMax(v); setCustom(""); }} className={`rounded-full px-4 py-2 text-sm transition ${priceMax === v && !custom ? "btn-gold" : "border border-xmas-gold/30 text-xmas-slate hover:text-xmas-snow"}`}>{t("gifts.up_to")} {formatMoney(symbol, v)}</button>
        ))}
        <div className="flex items-center gap-1">
          <span className="text-xs text-xmas-slate">{symbol}</span>
          <MoneyInput value={custom} onChange={(v) => { setCustom(v); setPriceMax(null); }} placeholder={t("gifts.custom")} testid="gift-price-custom"
            className="w-36 rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-2 py-1.5 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
        </div>
      </div>

      <div className="mb-8">
        <ProductSearchBar historyKey="gifts" testid="gift-search" onSearch={(q) => findReal(q)} maxLabel={effectiveMax ? fmt(effectiveMax) : null} />
      </div>

      {/* Suggestions (6 per category) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gift-suggestions">
        {shown.map((s) => (
          <div key={s.id} className="flex flex-col rounded-2xl border border-xmas-gold/15 bg-xmas-navycard p-5" data-testid={`gift-sugg-${s.id}`}>
            <h3 className="font-display text-lg text-xmas-snow">{s.name}</h3>
            <p className="mt-1 text-xs uppercase tracking-wider text-xmas-champagne">{t("gifts.why")}</p>
            <p className="text-sm text-xmas-slate">{s.why}</p>
            {s.price_est != null && <p className="mt-2 font-mono-num text-sm text-xmas-gold">~{formatMoney(symbol, s.price_est)} <span className="text-[10px] text-xmas-slate">({t("estimate")})</span></p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => findReal(s.name)} data-testid={`gift-find-real-${s.id}`} className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/30 px-3 py-1.5 text-xs text-xmas-snow hover:border-xmas-gold"><Search className="h-3 w-3" /> {t("gifts.find_real")}</button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!prodModal} onClose={() => setProdModal(null)} title={t("prod.suggestions_title")} testid="gift-products-modal">
        <ProductResults loading={prodModal?.loading} result={prodModal?.result} onChoose={chooseProduct} />
      </Modal>
      {budgetGuardNode}
    </div>
  );
}

function RecipientEdit({ value, placeholder, testid, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const commit = () => { setEditing(false); const v = val.trim(); if (v !== (value || "")) onChange(v); };
  if (editing) {
    return (
      <input autoFocus value={val} maxLength={40} placeholder={placeholder} data-testid={`${testid}-input`}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(value || ""); setEditing(false); } }}
        className="w-32 rounded-md border border-xmas-gold/40 bg-xmas-navy/60 px-2 py-0.5 text-[11px] text-xmas-snow focus:border-xmas-gold focus:outline-none" />
    );
  }
  return (
    <button type="button" onClick={() => { setVal(value || ""); setEditing(true); }} data-testid={testid} title={placeholder}
      className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/20 px-2 py-0.5 text-[11px] text-xmas-champagne transition hover:border-xmas-gold hover:text-xmas-gold">
      <UserRound className="h-3 w-3" /> {value || placeholder} <Pencil className="h-2.5 w-2.5 opacity-70" />
    </button>
  );
}
